import { memo, useEffect, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import {
  tableActionHeaderCellStyles,
  tableHeaderCellStyles,
} from "@/components/GenericTable/styles";
import type { PatientCommunicationRow } from "../aggregations";
import { formatPatientTaxIdentifierDisplay } from "../helpers/formatCrmCommunicationsDisplay";
import { useSetSelection } from "../hooks/useSetSelection";
import { useTablePagination } from "../hooks/useTablePagination";
import { formatPatientListLabel } from "../patientLabels";
import type { OnOpenCommunicationsWhatsAppSend } from "../types/sendMessage";
import CommunicationPatientSelectionBar from "./CommunicationPatientSelectionBar";
import CommunicationsTablePagination from "./CommunicationsTablePagination";
import PatientNameCell from "./PatientNameCell";
import {
  PatientBirthDateTableCellContent,
  PatientContactTableCellContent,
  PatientTaxIdentifierTableCellContent,
} from "./PatientSummaryTableCells";
import {
  communicationsRankFilledSearchFieldSx,
  communicationsRankRowSx,
  communicationsRankTableBodyCellSx,
  communicationsRankTableToolbarSx,
} from "./communicationsSubsectionStyles";

export interface CommunicationsPatientSendRankTableProps {
  patientMessageGroups: PatientCommunicationRow[];
  totalSends: number;
  vetApp?: boolean;
  petNamesByTutorLogicalId?: Map<string, string[]>;
  detailPatientId: string | null;
  onOpenDetail: (row: PatientCommunicationRow) => void;
  onOpenSendMessage: OnOpenCommunicationsWhatsAppSend;
}

function normalizeSearch(value: string): string {
  return value.trim().toLowerCase();
}

function matchesPatientSearch(
  { patient }: PatientCommunicationRow,
  query: string,
): boolean {
  const label = formatPatientListLabel(patient).toLowerCase();
  const phone = (patient.phone ?? "").toLowerCase();
  const tax = (patient.taxIdentifierValue ?? "").toLowerCase();
  const taxDisplay = formatPatientTaxIdentifierDisplay(
    patient.taxIdentifierValue,
  ).toLowerCase();

  return (
    label.includes(query) ||
    phone.includes(query) ||
    tax.includes(query) ||
    taxDisplay.includes(query)
  );
}

function matchesVetSearch(params: {
  group: PatientCommunicationRow;
  petNames: string[] | undefined;
  query: string;
}): boolean {
  const { group, petNames, query } = params;
  if (matchesPatientSearch(group, query)) return true;
  const petsJoined = (petNames ?? []).join(" ").toLowerCase();
  return petsJoined.includes(query);
}

/**
 * Lista de pacientes que receberam mensagens, ordenada por volume de envios.
 */
const CommunicationsPatientSendRankTable = memo(
  ({
    patientMessageGroups,
    totalSends: _totalSends,
    vetApp = false,
    petNamesByTutorLogicalId,
    detailPatientId,
    onOpenDetail,
    onOpenSendMessage,
  }: CommunicationsPatientSendRankTableProps) => {
    const [searchQuery, setSearchQuery] = useState("");

    const { selectedIds, selectedCount, toggle, toggleAll, clear, has } =
      useSetSelection();

    const contactedGroups = useMemo(
      () =>
        [...patientMessageGroups]
          .filter((g) => g.messages.length > 0)
          .sort((a, b) => b.messages.length - a.messages.length),
      [patientMessageGroups],
    );

    const filteredGroups = useMemo(() => {
      const q = normalizeSearch(searchQuery);
      if (!q) return contactedGroups;
      return contactedGroups.filter((group) => {
        if (!vetApp) return matchesPatientSearch(group, q);
        const petNames = petNamesByTutorLogicalId?.get(group.patient.patientId);
        return matchesVetSearch({ group, petNames, query: q });
      });
    }, [contactedGroups, searchQuery, petNamesByTutorLogicalId, vetApp]);

    const filteredPatientIds = useMemo(
      () => filteredGroups.map((g) => g.patient.patientId),
      [filteredGroups],
    );

    const tableResetKey = useMemo(
      () => `${filteredGroups.length}-${searchQuery}`,
      [filteredGroups.length, searchQuery],
    );

    const {
      paginatedItems: paginatedGroups,
      page,
      rowsPerPage,
      totalCount,
      handlePageChange,
      handleRowsPerPageChange,
    } = useTablePagination(filteredGroups, tableResetKey, {
      defaultRowsPerPage: 10,
    });

    const allSelected =
      filteredPatientIds.length > 0 &&
      filteredPatientIds.every((id) => selectedIds.has(id));
    const someSelected = filteredPatientIds.some((id) => selectedIds.has(id));

    useEffect(() => {
      clear();
    }, [patientMessageGroups, clear]);

    const openSendForIds = (ids: Iterable<string>) => {
      const patients = [];
      for (const id of ids) {
        const row = patientMessageGroups.find(
          (g) => g.patient.patientId === id,
        );
        if (row) patients.push(row.patient);
      }
      if (patients.length > 0) {
        onOpenSendMessage({ patients });
      }
    };

    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={communicationsRankTableToolbarSx}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {vetApp
                  ? "Tutores que receberam mensagens"
                  : "Pacientes que receberam mensagens"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {vetApp
                  ? "Destinatários reais dos envios FluxZap (tutor por CPF)."
                  : "Somente pacientes que receberam mensagens via Fluxzap."}
              </Typography>
            </Stack>
            <TextField
              fullWidth
              variant="filled"
              label="Filtrar na lista"
              placeholder={
                vetApp
                  ? "Nome do pet, nome/telefone/CPF do tutor"
                  : "Nome, telefone ou CPF do paciente"
              }
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              sx={communicationsRankFilledSearchFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="disabled" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Box>

        <Box sx={{ px: 1.5, py: 1.25 }}>
          <CommunicationPatientSelectionBar
            selectedCount={selectedCount}
            onSendMessage={() => openSendForIds(selectedIds)}
            onClearSelection={clear}
          />
        </Box>

        {filteredGroups.length === 0 ? (
          <Box sx={{ px: 1.5, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery.trim()
                ? "Nenhum paciente que recebeu mensagem corresponde à busca."
                : "Nenhum paciente com mensagem recebida registrada até o momento."}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table aria-label="Pacientes que receberam mensagens, ordenados por quantidade de envios">
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      sx={tableHeaderCellStyles}
                      align="center"
                    >
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={() => toggleAll(filteredPatientIds)}
                        inputProps={{
                          "aria-label":
                            "Selecionar todos os pacientes que receberam mensagens",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={tableHeaderCellStyles}
                      align="center"
                    >
                      <Typography variant="subtitle2">
                        {vetApp ? "Tutor" : "Paciente"}
                      </Typography>
                    </TableCell>
                    {vetApp ? (
                      <TableCell sx={tableHeaderCellStyles} align="center">
                        <Typography variant="subtitle2">Pet(s)</Typography>
                      </TableCell>
                    ) : null}
                    <TableCell
                      sx={tableHeaderCellStyles}
                      align="center"
                    >
                      <Typography variant="subtitle2">Contato</Typography>
                    </TableCell>
                    {!vetApp ? (
                      <TableCell sx={tableHeaderCellStyles} align="center">
                        <Typography variant="subtitle2">Nascimento</Typography>
                      </TableCell>
                    ) : null}
                    <TableCell
                      sx={tableHeaderCellStyles}
                      align="center"
                    >
                      <Typography variant="subtitle2">CPF</Typography>
                    </TableCell>
                    <TableCell
                      sx={tableHeaderCellStyles}
                      align="center"
                    >
                      <Typography variant="subtitle2">Envios</Typography>
                    </TableCell>
                    <TableCell sx={tableActionHeaderCellStyles} align="center">
                      <Typography variant="subtitle2">Ações</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedGroups.map(({ patient, messages }) => {
                    const checked = has(patient.patientId);
                    const isDetailOpen = detailPatientId === patient.patientId;
                    const highlighted = checked || isDetailOpen;
                    const petNames = vetApp
                      ? petNamesByTutorLogicalId?.get(patient.patientId)
                      : undefined;

                    return (
                      <TableRow
                        key={patient.patientId}
                        hover
                        selected={highlighted}
                        sx={communicationsRankRowSx(highlighted, {
                          borderWidth: isDetailOpen ? 4 : 3,
                        })}
                      >
                        <TableCell
                          padding="checkbox"
                          align="center"
                          sx={communicationsRankTableBodyCellSx}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Checkbox
                            checked={checked}
                            onChange={() => toggle(patient.patientId)}
                            inputProps={{
                              "aria-label": `Selecionar ${formatPatientListLabel(patient)}`,
                            }}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={communicationsRankTableBodyCellSx}
                        >
                          <Tooltip title={formatPatientListLabel(patient)}>
                            <Box
                              sx={{
                                maxWidth: { xs: 140, sm: 220, md: 280 },
                                mx: "auto",
                                textAlign: "center",
                              }}
                            >
                              <PatientNameCell
                                patient={patient}
                                primaryVariant="body2"
                                primaryFontWeight={400}
                              />
                            </Box>
                          </Tooltip>
                        </TableCell>
                        {vetApp ? (
                          <TableCell
                          align="center"
                          sx={communicationsRankTableBodyCellSx}
                        >
                            <Typography variant="body2" color="text.secondary">
                              {petNames?.length
                                ? petNames.join(", ")
                                : "—"}
                            </Typography>
                          </TableCell>
                        ) : null}
                        <TableCell
                          align="center"
                          sx={communicationsRankTableBodyCellSx}
                        >
                          <PatientContactTableCellContent patient={patient} />
                        </TableCell>
                        {!vetApp ? (
                          <TableCell
                          align="center"
                          sx={communicationsRankTableBodyCellSx}
                        >
                            <PatientBirthDateTableCellContent patient={patient} />
                          </TableCell>
                        ) : null}
                        <TableCell
                          align="center"
                          sx={communicationsRankTableBodyCellSx}
                        >
                          <PatientTaxIdentifierTableCellContent
                            patient={patient}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={communicationsRankTableBodyCellSx}
                        >
                          <Typography variant="body2">
                            {messages.length}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ textAlign: "center", verticalAlign: "middle" }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="center"
                            alignItems="center"
                          >
                            <Tooltip title="Ver detalhes do paciente e mensagens enviadas">
                              <IconButton
                                aria-label={`Ver detalhes de ${formatPatientListLabel(patient)}`}
                                onClick={() =>
                                  onOpenDetail({ patient, messages })
                                }
                              >
                                <VisibilityOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Enviar mensagem via WhatsApp">
                              <IconButton
                                color="primary"
                                aria-label={`Enviar mensagem via WhatsApp para ${formatPatientListLabel(patient)}`}
                                onClick={() =>
                                  onOpenSendMessage({ patients: [patient] })
                                }
                              >
                                <WhatsAppIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                px: 1.5,
                py: 0.5,
              }}
            >
              <CommunicationsTablePagination
                count={totalCount}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </>
        )}
      </Paper>
    );
  },
);

CommunicationsPatientSendRankTable.displayName =
  "CommunicationsPatientSendRankTable";

export default CommunicationsPatientSendRankTable;
